import React from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { setConsent } from "../lib/consent";
import { BRAND } from "../data/content";

/**
 * Privacy policy — covers what is collected, why, how long it's retained,
 * who processes it, and how to request deletion. Referenced from the consent
 * banner and the footer "Cookie preferences" link.
 */
export default function Privacy() {
  useDocumentMeta({
    title: "Privacy · JJ Argent",
    description:
      "How JJ Argent collects, uses and retains information from visitors to jjargent.com — plus how to change your consent or request deletion.",
    path: "/privacy",
  });

  const changePref = (value) => () => setConsent(value);

  return (
    <main id="main" data-testid="privacy-page" className="bg-[#0B0B0C] min-h-screen">
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <p className="eyebrow">Legal</p>
        <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#FAF9F6]">
          Privacy policy
        </h1>
        <p className="mt-6 text-[#A9A9A6] text-sm">
          Last updated: {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-12 space-y-10 text-[#C7C4BC] leading-relaxed text-[15px] prose-jj">
          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">1. What this covers</h2>
            <p className="mt-3">
              This policy explains what personal information is collected when you visit jjargent.com, why it is
              collected, how long it is kept, and how you can access, change or delete it.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">2. What is collected</h2>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>Analytics (with consent).</strong> Pageviews, referrer, approximate location (country/city), device type and interaction events, via PostHog. No advertising trackers. Session recording is off and will not be enabled without a separate, explicit opt-in.</li>
              <li><strong>Form submissions.</strong> Name, email and message when you use the contact, tailored-CV request, guestbook or newsletter forms. Only what you type in.</li>
              <li><strong>Bot protection.</strong> A Cloudflare Turnstile token, used once to verify the form submission is not automated. No data about you is retained by this check.</li>
              <li><strong>Technical logs.</strong> IP address and request timestamps at the edge (Cloudflare), used for abuse prevention. Rotated on Cloudflare's schedule.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">3. Why it's collected</h2>
            <p className="mt-3">
              Analytics tell me which content is useful so I can improve it. Form submissions are used solely to reply to you. Logs are used to prevent abuse.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">4. Who processes it</h2>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li><strong>PostHog</strong> (via Emergent) — analytics events. <a className="underline" href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog privacy</a>.</li>
              <li><strong>Resend</strong> — future outbound transactional email.</li>
              <li><strong>Cloudflare</strong> — CDN, bot protection, edge logs.</li>
              <li><strong>Mongo Atlas / Emergent-managed database</strong> — where form submissions land.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">5. How long it's kept</h2>
            <ul className="mt-3 list-disc pl-6 space-y-2">
              <li>Form submissions: kept until the enquiry is resolved, then deleted on request or at most within 24 months.</li>
              <li>Analytics events: 12 months, then rolled up into aggregate counts only.</li>
              <li>Session recordings: not applicable (off).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">6. Your rights</h2>
            <p className="mt-3">
              You can ask for a copy of what's been collected about you, ask for it to be corrected, or ask for it to be deleted, at any time. Email {" "}
              <a className="underline text-[#D8A936]" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> and it will be actioned within 30 days.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#FAF9F6]">7. Change your consent</h2>
            <p className="mt-3">
              You can change your analytics consent at any time — either choice takes effect immediately for the current browser.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={changePref("accepted")}
                data-testid="privacy-accept"
                className="px-5 py-2.5 border border-[#D8A936] bg-[#D8A936] text-[11px] tracking-[0.18em] uppercase font-display text-[#0B0B0C] hover:bg-[#B98B24] hover:border-[#B98B24] transition-colors"
              >
                Accept analytics
              </button>
              <button
                type="button"
                onClick={changePref("rejected")}
                data-testid="privacy-reject"
                className="px-5 py-2.5 border border-[#343437] text-[11px] tracking-[0.18em] uppercase font-display text-[#FAF9F6] hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
              >
                Reject analytics
              </button>
            </div>
          </section>

          <section>
            <p className="pt-8 border-t border-[#343437] text-sm text-[#A9A9A6]">
              Questions? <Link to="/contact" className="underline text-[#D8A936]">Get in touch</Link>.
            </p>
          </section>
        </div>
      </section>
    </main>
  );
}
